<template>
  <teleport to="#headerPageContent">
    <HeaderBarItem v-if="charts.length === 0">
      <div :class="$style.help">
        Build your dashboard by adding charts
      </div>
    </HeaderBarItem>
    <HeaderBarItem>
      <HeaderIconButton @click="addChart">
        <template #icon>
          <PlusCircleSvg />
        </template>
        <template #label>
          Add a chart
        </template>
      </HeaderIconButton>
    </HeaderBarItem>
    <HeaderBarItem>
      <DashboardLayoutDropdown
        v-model="chartsPerRow"
        :options="$options.LAYOUT_OPTIONS"
      />
    </HeaderBarItem>
    <HeaderBarItem>
      <DashboardSettingsDropdown
        :model-value="dashboardSettings"
        @update:modelValue="saveSettings"
      />
    </HeaderBarItem>
    <HeaderBarItem>
      <HeaderIconButton
        :is-danger="true"
        @click="removeDashboard"
      >
        <template #label>
          Delete dashboard
        </template>
      </HeaderIconButton>
    </HeaderBarItem>
  </teleport>

  <div :class="$style.page">
    <BaseContainer is-paddingless>
      <BaseLoader
        is-block
        :is-active="isLoading"
      >
        <div
          :class="[
            $style.chartsGrid,
            $style[`perRow${chartsPerRow}`],
            $style[`last${showGridItems % chartsPerRow}`],
          ]"
        >
          <div
            v-for="(chart, index) in charts"
            :key="chart.id"
            :class="$style.chartBlock"
          >
            <DashboardChart
              :ref="pushChartRef"
              :model-value="chart"
              :ratio="chartRatio"
              @update:modelValue="saveChart"
              @remove="removeChart(chart)"
              @move="showMoveOverlays(chart)"
            />
            <div
              v-if="movingChartId"
              :class="[
                $style.movingOverlay,
              ]"
            >
              <BaseButton
                v-if="movingChartId === chart.id"
                variant="danger"
                @click="cancelMoving"
              >
                Cancel Moving
              </BaseButton>
              <BaseButton
                v-else
                @click="moveChart(index)"
              >
                Move Here
              </BaseButton>
            </div>
          </div>
          <div
            v-for="(_, index) in showEmptyGridItemsArray"
            :key="index"
            :class="$style.chartBlock"
            :style="{ paddingTop: `${100 / chartRatio}%` }"
          />
        </div>
      </BaseLoader>
    </BaseContainer>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import { mapState } from 'vuex';
import BaseContainer from '@/components/base/Container';
import BaseButton from '@/components/base/Button';
import BaseLoader from '@/components/base/Loader';
import HeaderBarItem from '@/components/HeaderBarItem';
import HeaderIconButton from '@/components/HeaderIconButton';
import DashboardLayoutDropdown from '@/components/DashboardLayoutDropdown';
import DashboardSettingsDropdown from '@/components/DashboardSettingsDropdown';
import DashboardChart from '@/components/DashboardChart';
import PlusCircleSvg from '@/assets/images/icons/plus-circle.svg?inline';
import * as chartsTools from '@/charts';

export default {
  components: {
    BaseContainer,
    BaseLoader,
    BaseButton,
    HeaderIconButton,
    DashboardLayoutDropdown,
    DashboardSettingsDropdown,
    HeaderBarItem,
    PlusCircleSvg,
    DashboardChart,
  },
  LAYOUT_OPTIONS: [
    {
      label: '2 charts per row',
      value: 2,
    },
    {
      label: '3 charts per row',
      value: 3,
    },
    {
      label: '4 charts per row',
      value: 4,
    },
  ],
  data(vm) {
    return {
      charts: [],
      chartsPerRow: vm.$options.LAYOUT_OPTIONS[0].value,
      chartsRefs: [],
      movingChartId: null,
      isLoading: false,
      dashboardSettings: {},
    };
  },
  computed: {
    ...mapState('user', ['user']),
    chartRatio() {
      return this.chartsPerRow === 2 ? 2 : 1;
    },
    showGridItems() {
      return Math.max(
        this.chartsPerRow * 2,
        this.charts.length + this.chartsPerRow - (this.charts.length % this.chartsPerRow),
      );
    },
    showEmptyGridItemsArray() {
      return new Array(this.showGridItems - this.charts.length);
    },
  },
  created() {
    this.loadDashboard();
  },
  methods: {
    async loadDashboard() {
      this.loadCharts();
      const res = await this.$apollo.query({
        query: gql`query ($dashboardId: Int!) {
          dashboards_by_pk(id: $dashboardId) {
            id
            name
            description
            icon
          }
        }`,
        variables: {
          dashboardId: this.$route.params.dashboardId,
        },
      });

      const data = res.data.dashboards_by_pk;

      this.dashboardSettings.name = data.name;
      this.dashboardSettings.description = data.description;
      this.dashboardSettings.icon = data.icon;
    },
    async loadCharts() {
      this.isLoading = true;

      const res = await this.$apollo.query({
        query: gql`query ($dashboardId: Int!) {
          charts: dashboard_charts(where: {dashboard_id: {_eq: $dashboardId}}, order_by: {order: asc}) {
            id
            name
            source
            type
            data
            order
            stacked
          }
        }`,
        variables: {
          dashboardId: this.$route.params.dashboardId,
        },
      });

      this.charts = res.data.charts.map((x) => ({
        id: x.id,
        name: x.name,
        order: x.order,
        source: x.source,
        type: x.type,
        parameters: x.data,
        stacked: x.stacked === null ? false : x.stacked,
      }));

      this.isLoading = false;
    },
    addChart() {
      this.charts.push({
        id: Math.random(), // Until saved
        name: null,
        source: null,
        type: chartsTools.TYPE.line,
        parameters: null,
        order: this.charts.length > 0
          ? this.charts[this.charts.length - 1].order + 1
          : 1,
        isNew: true,
        isSaving: false,
        stacked: false,
      });

      this.$nextTick(() => {
        this.chartsRefs[this.chartsRefs.length - 1].openActions();
      });
    },
    async saveChart(model) {
      const chartIndex = this.charts.findIndex((c) => c.id === model.id);
      const currentModel = this.charts[chartIndex];
      currentModel.isSaving = true;

      if (model.isNew) {
        const res = await this.$apollo.mutate({
          mutation: gql`mutation (
            $data: jsonb,
            $name: String,
            $source: String,
            $type: String,
            $dashboardId: Int!,
            $order: Int,
            $stacked: Boolean!
          ) {
            insert: insert_dashboard_charts_one(object: {
              data: $data,
              name: $name,
              source: $source,
              type: $type,
              dashboard_id: $dashboardId,
              order: $order,
              stacked: $stacked,
            }
            ) {
              id
            }
          }`,
          variables: {
            name: model.name,
            source: model.source,
            type: model.type,
            order: model.order,
            dashboardId: this.$route.params.dashboardId,
            data: model.parameters,
            stacked: model.stacked,
          },
        });

        this.charts.splice(chartIndex, 1, {
          ...model,
          id: res.data.insert.id,
          isNew: false,
        });
        return;
      }

      await this.$apollo.mutate({
        mutation: gql`mutation (
            $id: Int!, $data: jsonb, $name: String, $source: String, $type: String
          ) {
            update: update_dashboard_charts_by_pk(
              pk_columns: {id: $id},
              _set: {name: $name, type: $type, source: $source, data: $data}
            ) {
              id
            }
          }`,
        variables: {
          id: model.id,
          name: model.name,
          source: model.source,
          type: model.type,
          data: model.parameters,
        },
      });

      this.charts.splice(chartIndex, 1, model);
    },
    pushChartRef(ref) {
      this.chartsRefs.push(ref);
    },
    async removeChart(chart) {
      // eslint-disable-next-line no-param-reassign
      chart.isSaving = true;

      if (!chart.isNew) {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: Int!) {
            delete_dashboard_charts_by_pk(id: $id) {
              id
            }
          }`,
          variables: {
            id: chart.id,
          },
        });
      }

      const index = this.charts.findIndex((c) => c.id === chart.id);
      this.charts.splice(index, 1);
    },
    showMoveOverlays(chart) {
      this.movingChartId = chart.id;
    },
    cancelMoving() {
      this.movingChartId = null;
    },
    async moveChart(toIndex) {
      this.isLoading = true;
      const currentIndex = this.charts.findIndex((c) => c.id === this.movingChartId);
      const chart = this.charts.splice(currentIndex, 1)[0];
      this.charts.splice(toIndex, 0, chart);
      this.movingChartId = null;
      this.charts.forEach((data, index) => {
        // eslint-disable-next-line no-param-reassign
        data.order = index + 1;
      });

      const mutations = this.charts.reduce(
        (res, data) => `${res}
      chart${data.id}: update_dashboard_charts_by_pk(
              pk_columns: {id: ${data.id}}, _set: {order: ${data.order}}
            ) {
              id
            }`,
        '',
      );

      await this.$apollo.mutate({
        mutation: gql`mutation {
          ${mutations}
        }`,
      });

      this.isLoading = false;
    },
    async saveSettings(settings) {
      this.isLoading = true;
      await this.$apollo.mutate({
        mutation: gql`mutation($id: Int!, $name: String, $description: String, $icon: Int) {
          update_dashboards_by_pk(pk_columns: {id: $id}, _set: {name: $name, description: $description, icon: $icon}) {
            id
            name
            description
            icon
          }
        }`,
        variables: {
          id: this.$route.params.dashboardId,
          name: settings.name,
          description: settings.description,
          icon: settings.icon,
        },
      });

      this.$bus.emit('breadcrumbs:update');

      this.isLoading = false;
    },
    async removeDashboard() {
      this.isLoading = true;
      await this.$apollo.mutate({
        mutation: gql`mutation($id: Int!) {
          delete_dashboards_by_pk(id: $id) {
            id
          }
        }`,
        variables: {
          id: this.$route.params.dashboardId,
        },
      });

      await this.$apollo.mutate({
        mutation: gql`mutation ($dashboardId: Int!) {
          charts: delete_dashboard_charts(where: {dashboard_id: {_eq: $dashboardId}}) {
            returning {
              id
            }
          }
        }`,
        variables: {
          dashboardId: this.$route.params.dashboardId,
        },
      });

      await this.$router.push({ name: 'Dashboards' });
    },
  },
};
</script>

<style lang="scss" module>
.page {
  flex-grow: 1;
  padding-bottom: 20px;
}

.help {
  position: relative;
  padding: 9px 12px 8px;
  font-size: 13px;
  font-weight: 500;
  color: $white;
  background-color: $title;
  border-radius: 10px;

  &::after {
    position: absolute;
    top: 50%;
    right: -6px;
    display: inline-block;
    width: 0;
    height: 0;
    content: '';
    border-color: transparent transparent transparent $title;
    border-style: solid;
    border-width: 6.5px 0 6.5px 6px;
    transform: translateY(-50%);
  }
}

.chartsGrid {
  display: grid;
  padding: 0 20px;

  &.perRow2 {
    grid-template-columns: repeat(2, calc(100% / 2));

    .chartBlock {

      &:nth-child(2n) {
        border-right: 0;
      }
    }
  }

  &.perRow3 {
    grid-template-columns: repeat(3, calc(100% / 3));

    .chartBlock {

      &:nth-child(3n) {
        border-right: 0;
      }
    }
  }

  &.perRow4 {
    grid-template-columns: repeat(4, calc(100% / 4));

    .chartBlock {

      &:nth-child(4n) {
        border-right: 0;
      }
    }
  }

  &.perRow2,
  &.perRow3,
  &.perRow4 {

    &.last0,
    &.last1 {

      .chartBlock {

        &:nth-last-child(1) {
          border-bottom: 0;
        }
      }
    }

    &.last0 {

      .chartBlock {

        &:nth-last-child(2) {
          border-bottom: 0;
        }
      }
    }
  }

  &.perRow3,
  &.perRow4 {

    &.last0 {

      .chartBlock {

        &:nth-last-child(3) {
          border-bottom: 0;
        }
      }
    }

    &.last2 {

      .chartBlock {

        &:nth-last-child(1),
        &:nth-last-child(2) {
          border-bottom: 0;
        }
      }
    }
  }

  &.perRow4 {

    &.last0 {

      .chartBlock {

        &:nth-last-child(4) {
          border-bottom: 0;
        }
      }
    }

    &.last3 {

      .chartBlock {

        &:nth-last-child(1),
        &:nth-last-child(2),
        &:nth-last-child(3) {
          border-bottom: 0;
        }
      }
    }
  }
}

.chartBlock {
  position: relative;
  display: flex;
  border-right: 1px solid rgba($disabled, 0.4);
  border-bottom: 1px solid rgba($disabled, 0.4);

  > * {
    flex-grow: 1;
  }
}

.movingOverlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba($disabled, 0.6);
}
</style>
